/**
 * Hand-rolled customer-portal i18n primitive — no i18next dep, ~150 LOC.
 *
 * Public surface:
 *   - <LocaleProvider initialLocale={...}>{children}</LocaleProvider>
 *   - useLocale() → { locale, setLocale, supportedLocales }
 *   - t(key, vars?) — pure lookup against the active catalogue
 *   - useT() — hook that returns a `t` bound to the active locale
 *   - <Trans i18nKey="..." vars={{...}} /> — rendering component
 *
 * The catalogues live in `./locales/<tag>.ts`; the union of valid keys
 * (TranslationKey) is derived from the en-GB catalogue at compile time
 * so a typo in `t('foo.bar')` is a TypeScript error rather than a silent
 * miss at runtime.
 *
 * Locale precedence (resolved by the caller before mounting the
 * provider, typically inside CustomerApp):
 *   customer_users.locale > accounts.locale > navigator.language > 'en-GB'
 *
 * `t()` falls back to en-GB if the key is missing in the requested
 * locale; missing-everywhere yields the dotted key as a debug aid.
 */

import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DEFAULT_LOCALE, type Locale, SUPPORTED_LOCALES, isSupportedLocale } from './locales';
import { DE_DE } from './locales/de-DE';
import { EN_GB } from './locales/en-GB';
import { EN_US } from './locales/en-US';
import { ES_US } from './locales/es-US';
import { FR_CA } from './locales/fr-CA';

// ---------------------------------------------------------------------------
// Type-level: derive `foo.bar` keys from the en-GB catalogue shape.
// ---------------------------------------------------------------------------

type Source = typeof EN_GB;

// Recursive walk: build "a.b.c" keys from nested string maps.
type LeafPaths<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : T[K] extends Record<string, unknown>
      ? LeafPaths<T[K], `${Prefix}${K}.`>
      : never;
}[keyof T & string];

export type TranslationKey = LeafPaths<Source>;

// A loose-typed map for non-source catalogues — same shape, looser values
// so missing keys aren't a hard error at the file level (we fall back to
// en-GB in `t()` instead, which is the right runtime behaviour).
export type TranslationMap = {
  [section: string]: {
    [key: string]: string;
  };
};

// ---------------------------------------------------------------------------
// Catalogue registry
// ---------------------------------------------------------------------------

const CATALOGUES: Record<Locale, TranslationMap> = {
  'en-GB': EN_GB as TranslationMap,
  'en-US': EN_US,
  'es-US': ES_US,
  'fr-CA': FR_CA,
  'de-DE': DE_DE,
};

// ---------------------------------------------------------------------------
// Pure lookup + substitution
// ---------------------------------------------------------------------------

export type TVars = Record<string, string | number>;

function lookup(catalogue: TranslationMap, key: string): string | undefined {
  const parts = key.split('.');
  let cur: unknown = catalogue;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return undefined;
    }
  }
  return typeof cur === 'string' ? cur : undefined;
}

/**
 * Substitute `{{ varname }}` (whitespace-tolerant) with vars[varname].
 * Unknown vars render verbatim (`{{ x }}`) so they're visible to the
 * developer during translation review.
 */
export function substitute(template: string, vars?: TVars): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (m, name: string) => {
    const v = vars[name];
    if (v === undefined || v === null) return m;
    return String(v);
  });
}

/**
 * Pure translation function. Falls back to en-GB if missing in the
 * requested locale; falls back to the key itself if missing everywhere.
 */
export function translate(locale: Locale, key: string, vars?: TVars): string {
  const cat = CATALOGUES[locale] ?? CATALOGUES[DEFAULT_LOCALE];
  let hit = lookup(cat, key);
  if (hit === undefined && locale !== DEFAULT_LOCALE) {
    hit = lookup(CATALOGUES[DEFAULT_LOCALE], key);
  }
  if (hit === undefined) return key; // debug-visible miss
  return substitute(hit, vars);
}

// ---------------------------------------------------------------------------
// Resolution helpers (called by CustomerApp at mount time)
// ---------------------------------------------------------------------------

export interface LocaleResolutionInput {
  customerLocale?: string | null | undefined;
  accountLocale?: string | null | undefined;
  navigatorLanguage?: string | null | undefined;
  supportedLocales?: readonly string[] | null | undefined;
}

/**
 * Walk customer → account → navigator → default until a supported locale
 * matches. `supportedLocales` (when non-null) narrows the candidate set
 * to the tenant's whitelist (accounts.supported_locales_json).
 */
export function resolveLocale(input: LocaleResolutionInput): Locale {
  const whitelist: ReadonlyArray<Locale> | null = (() => {
    if (!input.supportedLocales || input.supportedLocales.length === 0) return null;
    const valid = input.supportedLocales.filter(isSupportedLocale);
    return valid.length > 0 ? valid : null;
  })();
  const candidates = [input.customerLocale, input.accountLocale, input.navigatorLanguage];
  for (const c of candidates) {
    if (typeof c !== 'string') continue;
    // Exact match first
    if (isSupportedLocale(c) && (!whitelist || whitelist.includes(c))) return c;
    // Language-tag fallback: 'en' → first 'en-*' we support
    const lang = c.split('-')[0];
    if (lang) {
      const match = SUPPORTED_LOCALES.find(
        (l) => l.split('-')[0] === lang && (!whitelist || whitelist.includes(l)),
      );
      if (match) return match;
    }
  }
  // Default — narrow to whitelist if applicable
  if (whitelist && !whitelist.includes(DEFAULT_LOCALE)) return whitelist[0]!;
  return DEFAULT_LOCALE;
}

// ---------------------------------------------------------------------------
// React context + provider + hooks
// ---------------------------------------------------------------------------

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  supportedLocales: readonly Locale[];
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  initialLocale,
  supportedLocales,
  onLocaleChange,
  children,
}: {
  initialLocale: Locale;
  supportedLocales?: readonly Locale[];
  /** Fired on every setLocale call so consumers can persist (e.g. PATCH /api/customer/profile). */
  onLocaleChange?: (locale: Locale) => void;
  children: ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next);
      onLocaleChange?.(next);
    },
    [onLocaleChange],
  );
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      supportedLocales: supportedLocales ?? SUPPORTED_LOCALES,
    }),
    [locale, setLocale, supportedLocales],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const v = useContext(LocaleContext);
  if (!v) {
    // Safe default for components rendered outside a provider (e.g. unit
    // tests that don't wrap with LocaleProvider). Returns en-GB + a noop
    // setter so `t()` still works against the fallback catalogue.
    return {
      locale: DEFAULT_LOCALE,
      setLocale: () => {},
      supportedLocales: SUPPORTED_LOCALES,
    };
  }
  return v;
}

/**
 * Returns a `t` bound to the current locale. Memo'd per-locale so
 * downstream consumers' deps arrays stay stable.
 */
export function useT(): (key: TranslationKey, vars?: TVars) => string {
  const { locale } = useLocale();
  return useCallback((key: TranslationKey, vars?: TVars) => translate(locale, key, vars), [locale]);
}

// ---------------------------------------------------------------------------
// <Trans /> component
// ---------------------------------------------------------------------------

/**
 * Renders a translation by key. Equivalent to `{t(key, vars)}` — exists as
 * a JSX-native shorthand for tight callsites in markup.
 */
export function Trans({
  i18nKey,
  vars,
}: {
  i18nKey: TranslationKey;
  vars?: TVars;
}) {
  const t = useT();
  return <>{t(i18nKey, vars)}</>;
}
